import type { OpeningStats, Popularity } from '../../../shared/types';

/**
 * How often you meet an opening, 1-5, with the real numbers behind it.
 *
 * The bars exist for scanning a list; the tooltip carries the club-game count,
 * the share of the branch and when the figures were taken, because the meter is
 * a five-way compression of those and someone picking a repertoire deserves the
 * source. Openings authored since the last fetch have no stats and simply fall
 * back to the label alone rather than showing a confident-looking zero.
 */

const LABEL: Record<Popularity, string> = {
  1: 'Rare',
  2: 'Occasional',
  3: 'Regular',
  4: 'Common',
  5: 'Everywhere',
};

/** Compact game counts: "96.3M", "840k". The exact figure lives in the tooltip. */
export function formatGames(games: number): string {
  if (games >= 1_000_000) return `${(games / 1_000_000).toFixed(1)}M`;
  if (games >= 1_000) return `${Math.round(games / 1_000)}k`;
  return String(games);
}

export default function PopularityMeter({
  value,
  stats,
  compact = false,
}: {
  value: Popularity;
  stats?: OpeningStats;
  compact?: boolean;
}) {
  const level = Math.max(1, Math.min(5, value)) as Popularity;
  const title = stats
    ? `${LABEL[level]} — reached in ${stats.games.toLocaleString()} club games ` +
      `(${stats.source}, ${stats.ratings}, as of ${stats.fetchedAt}); ` +
      `played in ${stats.sharePercent}% of games from the previous move`
    : `${LABEL[level]} — how often you'll meet this at club level`;

  return (
    <span
      className={`pop${compact ? ' pop--compact' : ''}`}
      title={title}
      aria-label={`How often you'll meet it: ${LABEL[level]}`}
    >
      <span className="pop-bars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`pop-bar${i <= level ? ' pop-bar--on' : ''}`} />
        ))}
      </span>
      {!compact && (
        <span className="pop-label">
          {LABEL[level]}
          {stats && <span className="pop-games"> · {formatGames(stats.games)} games</span>}
        </span>
      )}
    </span>
  );
}
